import React from 'react';
import { ArrowRight, Code2, Megaphone, TrendingUp, Headphones } from 'lucide-react';
import { servicePillars } from '../../data/services';
import { ServicePillar } from '../../types/services';
import { Badge, Card } from '../ui';

export interface ServicesBentoGridProps {
  onSelectService: (serviceId: string) => void;
}

export const ServicesBentoGrid: React.FC<ServicesBentoGridProps> = ({ onSelectService }) => {
  const getIcon = (id: string) => {
    if (id.includes('development')) return <Code2 className="text-[#4a82c2]" size={24} />;
    if (id.includes('marketing')) return <Megaphone className="text-[#08adae]" size={24} />;
    if (id.includes('lead')) return <TrendingUp className="text-emerald-500" size={24} />;
    return <Headphones className="text-amber-500" size={24} />;
  };

  const numbers = ['01', '02', '03', '04'];
  const safePillars = servicePillars || [];

  return (
    <section id="services" className="py-12 sm:py-14 lg:py-16 relative bg-[#f5f9fc] dark:bg-[#080e1a] transition-colors">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        {/* Technook Original Header Layout */}
        <div className="max-w-3xl mb-8 sm:mb-10 text-left space-y-3">
          <Badge variant="accent" className="px-3.5 py-1 text-[11px] sm:text-xs uppercase tracking-wider">
            WHAT WE DO
          </Badge>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-[#184166] dark:text-white tracking-tight">
            Grow your business with our services.
          </h2>
          <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed">
            You deserve a partner that can do it all — from the product itself to the systems and support that keep your organization moving.
          </p>
        </div>

        {/* Technook Original 4 Division Cards with Number Watermarks */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 relative z-10">
          {safePillars.map((service: ServicePillar, idx: number) => {
            const listItems = service?.items || [];
            return (
              <Card
                key={service.id || idx}
                variant="interactive"
                className="relative overflow-hidden p-6 sm:p-8 bg-white dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800/80 shadow-xl group flex flex-col justify-between"
              >
                {/* Corner Watermark matching website-ui-ux */}
                <div className="technook-watermark" />

                <div className="space-y-4 sm:space-y-6 relative z-10">
                  <div className="flex items-center justify-between">
                    <div className="p-2.5 sm:p-3 rounded-2xl bg-sky-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
                      {getIcon(service.id || '')}
                    </div>
                    <span className="text-xl sm:text-2xl font-black text-[#08adae] font-mono opacity-80">
                      {numbers[idx] || '01'}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#184166] dark:text-white group-hover:text-[#4a82c2] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm mt-1.5 leading-relaxed">
                      {service.description}
                    </p>
                  </div>

                  {/* Key Line Items List */}
                  <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">Key Line Items:</span>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {listItems.slice(0, 4).map((item: string, dIdx: number) => (
                        <li key={dIdx} className="text-xs text-slate-700 dark:text-slate-300 flex items-center gap-1.5 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#08adae] shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Action Link */}
                <div className="pt-4 mt-4 sm:pt-6 sm:mt-6 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between relative z-10">
                  <span className="text-[11px] sm:text-xs font-semibold text-[#4a82c2] font-mono">Parañaque Delivery</span>
                  <button
                    onClick={() => onSelectService(service.id)}
                    className="text-xs font-bold text-[#184166] dark:text-white hover:text-[#4a82c2] flex items-center gap-1.5 group-hover:translate-x-1 transition-all"
                  >
                    <span>Inquire for {service.title}</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
